
import { defineComponent } from 'vue'
import { View, Hide, Close, UploadFilled, Download, DocumentCopy, CircleCheckFilled, Upload } from '@element-plus/icons-vue'
import { ElImageViewer } from 'element-plus'
import tableComponent from '@/components/table-component/index.vue'
import DrawerEmployeeDetail from '@/views/components/employee/drawer-employee-detail.vue'
import { getWorkersStatistics } from '@/api/workers'
import {
    queryUserDetailInfo,
    revokeResignLabel,
    queryEmployeeList,
    Employee,
    QueryEmployeeListResult,
    exportEmployeeList,
    getLabel
} from '@/api/resume'

interface SearchField {
    name: string
    prop: string
    type: string
    placeholder: string
    value: string | string[]
    defaultValue: string
    options?: Array<{ label: string; value: string }>
    defaultAttrs?: { label: string; value: string }
}

interface SearchList {
    [key: string]: SearchField
}

interface ButtonItem {
    name: string
    type: string
    plain: boolean
    borderColor?: string
    backgroundColor?: string
    color?: string
    icon?: string
    fn: () => void
}

interface TableHead {
    prop: string
    label?: string
    width?: number
    fixed?: string
}

interface PageInfo {
    pageNum: number
    pageSize: number
    total: number
}

interface Statistics {
    totalCount: number
    menCount: number
    womenCount: number
}

interface DetailEmployeeData {
    id: number
    name: string
    avatar: string
    idCard: string
    phone: string
    gender: string
    age: number
    address: string
    isFollowed: boolean
    healthCertStatus: string
    workRecords: any[]
    payrollRecords: any[]
}

export default defineComponent({
    name: 'EmploymentManagement',
    components: {
        View,
        Close,
        UploadFilled,
        Download,
        DocumentCopy,
        CircleCheckFilled,
        Upload,
        tableComponent,
        DrawerEmployeeDetail,
        ElImageViewer,
    },
    data() {
        return {
            uploadVisible: false,
            fileList: [] as File[],
            labelSearchQuery: '',
            maskedFields: {
                idCard: true,
                phone: true
            },
            searchList: {
                searchText: {
                    name: '员工姓名',
                    prop: 'searchText',
                    type: 'input',
                    placeholder: '请输入员工姓名',
                    value: '',
                    defaultValue: ''
                },
                phone: {
                    name: '手机号',
                    prop: 'phone',
                    type: 'input',
                    placeholder: '请输入手机号',
                    value: '',
                    defaultValue: ''
                },
                idCardNo: {
                    name: '身份证号',
                    prop: 'idCardNo',
                    type: 'input',
                    placeholder: '请输入身份证号',
                    value: '',
                    defaultValue: ''
                },
                labelIds: {
                    name: '员工标签',
                    prop: 'labelIds',
                    type: 'select',
                    placeholder: '请选择员工标签',
                    value: [] as any,
                    defaultValue: '' as any,
                    options: [],
                    defaultAttrs: {
                        label: 'label',
                        value: 'value'
                    }
                }
            } as SearchList,
            tableHead: [
                { prop: 'index', fixed: 'left' },
                { prop: 'workerName', label: '员工姓名', fixed: 'left', width: 120 },
                { prop: 'sex', label: '性别', width: 80 },
                { prop: 'age', label: '年龄', width: 80 },
                { prop: 'phone', label: '手机号', width: 150 },
                { prop: 'idCardNo', label: '身份证号', width: 200 },
                { prop: 'isLogout', label: '账号状态', width: 100 },
                { prop: 'isResign', label: '在职状态', width: 100 },
                { prop: 'labels', label: '员工标签', width: 200 },
                { prop: 'serveCompanyName', label: '最新派遣单位', width: 200 },
                { prop: 'lastSalaryTime', label: '最后领薪时间', width: 150 },
                { prop: 'createTime', label: '入职时间', width: 150 },
                { prop: 'operate', label: '操作', width: 150, fixed: 'right' }
            ] as TableHead[],
            tableData: [] as Employee[],
            pageInfo: {
                pageNum: 1,
                pageSize: 10,
                total: 5
            } as PageInfo,
            // 统计数据
            statistics: {
                totalCount: 128,
                menCount: 85,
                womenCount: 43
            } as Statistics,
            selects: [] as number[],
            // 弹窗相关
            currentRow: {} as Employee,
            // 图片预览
            imageViewerVisible: false,
            previewImages: [] as string[],
            // 标签弹窗相关
            tagDialogVisible: false,
            addTagDialogVisible: false,
            tagTargetRows: [] as Employee[], // 当前操作的员工列表
            hoveredTagId: null as number | null, // 鼠标悬停的标签ID
            newTagForm: {
                name: ''
            },
            // 员工详情抽屉
            detailDrawerVisible: false,
            detailEmployeeData: {} as DetailEmployeeData
        }
    },
    mounted() {
        this.loadLabels()
    },
    created() {
        this.getData()
    },
    computed: {
        filteredLabelOptions() {
            const allOptions = this.searchList.labelIds.options || []
            if (!this.labelSearchQuery) {
                return allOptions
            }
            return allOptions.filter(opt =>
                opt.label.toLowerCase().includes(this.labelSearchQuery.toLowerCase())
            )
        }
    },
    methods: {
        getData() {
            const query = this.createQuery()
            queryEmployeeList(query).then((res: QueryEmployeeListResult) => {
                this.tableData = res.list || []
                this.pageInfo.total = res.totalCount || 0
                this.statistics.totalCount = res.totalCount
                this.statistics.menCount = res.maleCount
                this.statistics.womenCount = res.femaleCount

            }).catch((error: Error) => {
                console.error('获取表格数据失败', error)
            })
        },
        getStatistics() {
            getWorkersStatistics().then((res) => {
                this.statistics = {
                    totalCount: res.count,
                    menCount: res.men,
                    womenCount: res.women
                }
            }).catch((error: Error) => {
                console.error('获取统计数据失败', error)
            })
        },
        createQuery(hasPage = true) {
            const query: Record<string, any> = {}
            if (hasPage) {
                query.pageNum = this.pageInfo.pageNum
                query.pageSize = this.pageInfo.pageSize
            }
            // 固定参数：获取离职员工数据
            query.isResign = '1'
            // 处理搜索条件
            for (const key in this.searchList) {
                const searchItem = this.searchList[key]
                if (searchItem.value) {
                    switch (key) {
                        case 'searchText':
                            query.searchText = searchItem.value
                            break
                        case 'phone':
                            query.phone = searchItem.value
                            break
                        case 'idCardNo':
                            query.idCardNo = searchItem.value
                            break
                        case 'labelIds':
                            if (Array.isArray(searchItem.value) && searchItem.value.length > 0) {
                                const validIds = searchItem.value.filter((id: any) => id !== '' && id != null)
                                if (validIds.length > 0) {
                                    query.labelIds = validIds.map((id: any) => Number(id)).join(',')
                                }
                            }
                            break
                        default:
                            query[searchItem.prop] = searchItem.value
                    }
                }
            }
            return query
        },
        handleSearch() {
            this.pageInfo.pageNum = 1
            this.getData()
        },
        handleReset() {
            // Reset all search fields to their default values
            for (const key in this.searchList) {
                const searchItem = this.searchList[key]
                searchItem.value = searchItem.defaultValue || ''
            }
            this.pageInfo.pageNum = 1
            this.getData()
        },
        handleSizeChange(ev: number) {
            this.pageInfo.pageSize = ev
            this.getData()
        },
        handleCurrentChange(ev: number) {
            this.pageInfo.pageNum = ev
            this.getData()
        },
        handleSelectionChange(selections: Employee[]) {
            this.selects = selections.map((item: Employee) => item.id)
        },
        formatIdCard(idCardNo: string) {
            if (!idCardNo) return '--'
            return idCardNo.replace(/(\d{3})\d+(\d{4})/, '$1****$2')
        },
        formatPhone(phone: string) {
            if (!phone) return '--'
            return phone.replace(/(\d{3})\d+(\d{4})/, '$1****$2')
        },
        truncateText(text: string, maxLength: number) {
            if (!text) return ''
            return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
        },
        handlePreviewImages(images: string[]) {
            this.previewImages = images.filter(img => img)
            if (this.previewImages.length > 0) {
                this.imageViewerVisible = true
            }
        },
        handleExport() {
            const exportQuery: any = this.createQuery(false)
            if (this.selects && this.selects.length > 0) {
                const selectedRows = this.tableData.filter((row: any) => this.selects.includes(row.id))
                const idCardNos = selectedRows
                    .map((row: any) => row.idCardNo)
                    .filter(Boolean)
                if (idCardNos.length > 0) {
                    exportQuery.idCardNos = idCardNos.join(',')
                }
            }
            exportEmployeeList(exportQuery).then((res: any) => {
                this.$store.commit('getLongPollingFile', res)
                this.$message.success('导出成功')
            })
        },
        handleBatchImport() {
            this.uploadVisible = true
            console.log('导入名单')
        },
        handleDetail(row: Employee) {
            // 查询员工详细信息
            queryUserDetailInfo({ userId: row.userId }).then((res: any) => {
                this.detailEmployeeData = {
                    id: row.id,
                    userId: row.userId,
                    name: res.userName,
                    userHeadUrl: res.userHeadUrl || '../assets/avatar.png',
                    idCardNo: res.idCardNo,
                    phone: res.phone,
                    gender: res.sex === '0' ? 'male' : 'female',
                    age: res.age || 0,
                    address: res.area,
                    jobIntention: res.jobIntention,
                    idCardBack: res.idCardBack,
                    idCardFront: res.idCardFront,
                    healthCard: res.healthCard,
                    bankCardImg: res.bankCardImg,
                    bankCardNo: res.bankCardNo,
                    educationExperience: res.educationExperiences,
                    labelList: res.labelList,
                }
                this.detailDrawerVisible = true
            }).catch((error: any) => {
                console.error('获取员工详情失败', error)
                this.$message.error('获取员工详情失败')
            })
        },
        handleBatchTag() {
            if (this.selects.length === 0) {
                this.$message.warning('请先选择员工')
                return
            }
            this.callBatchUndoApi(this.selects)
        },
        handleRowUndo(row: Employee) {
            this.callBatchUndoApi([row.id])
        },
        callBatchUndoApi(ids: number[]) {
            revokeResignLabel({ addressBookIds: ids }).then(() => {
                this.$message.success('撤销成功')
                this.getData()
                this.selects = []
            }).catch((error: Error) => {
                console.error('撤销失败', error)
                this.$message.error('撤销失败')
                this.selects = []
            })
        },
        handleAddCustomTag() {
            this.newTagForm.name = ''
            this.addTagDialogVisible = true
        },
        loadLabels() {
            getLabel().then((res: any) => {
                this.systemTags = (res.systemLabel || []).map((label: any) => ({
                    ...label,
                    checked: false
                }))
                this.customTags = (res.customLabel || []).map((label: any) => ({
                    ...label,
                    checked: false,
                    deleting: false
                }))
                // 更新搜索条件中的员工标签选项
                const allLabels = [...(res.systemLabel || []), ...(res.customLabel || [])]
                this.searchList.labelIds.options = allLabels.map((label: any) => ({
                    label: label.labelName,
                    value: label.id
                }))
            }).catch((error: any) => {
                console.error('加载标签失败', error)
            })
        },
        filterLabels(query: string) {
            this.labelSearchQuery = query
        },
        handleLabelChange() {
            const selectedCount = (this.searchList.labelIds.value as string[]).length
            if (selectedCount > 10) {
                (this.searchList.labelIds.value as string[]).pop()
                this.$message.warning('最多只能选择10个标签')
            }
        },
        isLabelDisabled(value: string) {
            const selectedCount = (this.searchList.labelIds.value as string[]).length
            const isSelected = (this.searchList.labelIds.value as string[]).includes(value)
            return selectedCount >= 10 && !isSelected
        }
    }
})
